import {
  FEATURE_AVAILABILITY_FEATURE_NAMES,
  FeatureName,
} from '@/lib/constants/maverick';
import { Region } from '@whoop/i18n';

export interface IFeatureAvailability {
  feature: {
    featureName: string;
  };
  available: {
    items: Array<{
      countryCode: Region;
    }>;
  };
  unavailable: {
    items: Array<{
      countryCode: Region;
    }>;
  };
  comingSoon: {
    items: Array<{
      countryCode: Region;
    }>;
  };
  tierAvailability: {
    items: Array<{
      tierName: string;
    }>;
  };
}

export enum FeatureStatusType {
  AVAILABLE = 'available',
  COMING_SOON = 'comingSoon',
  UNAVAILABLE = 'unavailable',
}

export interface FeatureStatus {
  isAvailable: boolean;
  isComingSoon: boolean;
  isUnavailable: boolean;
}

// Constants for ECG and IHRN feature availability special cases
export const ECG_IHRN_UNAVAILABLE_COUNTRIES = new Set([
  'FO',
  'GG',
  'IM',
  'JE',
  'MC',
  'GL',
]);

/**
 * @description This class is used to check the feature availability for a given feature and region.
 * @description If a feature is not setup in contentful or not in any of the collections, methods will return false
 */
export class FeatureAvailability {
  private data: IFeatureAvailability[];

  constructor(data: IFeatureAvailability[]) {
    this.data = data || [];
  }

  private hasFeatureStatus(
    featureName: string,
    region: Region,
    statusType: FeatureStatusType,
  ): boolean {
    const feature = this.data.find(
      (feature) => feature.feature.featureName === featureName,
    );

    if (!feature) return false;

    const statusItems = feature[statusType].items;
    return statusItems.some((item) => item.countryCode === region);
  }

  isAvailable(featureName: FeatureName, region: Region): boolean {
    return this.hasFeatureStatus(
      featureName,
      region,
      FeatureStatusType.AVAILABLE,
    );
  }

  isComingSoon(featureName: FeatureName, region: Region): boolean {
    return this.hasFeatureStatus(
      featureName,
      region,
      FeatureStatusType.COMING_SOON,
    );
  }

  isUnavailable(featureName: FeatureName, region: Region): boolean {
    return this.hasFeatureStatus(
      featureName,
      region,
      FeatureStatusType.UNAVAILABLE,
    );
  }

  private handleOverrides(
    featureName: FeatureName,
    country: string,
  ): FeatureStatus | null {
    const isECGOrIHRN = [
      FEATURE_AVAILABILITY_FEATURE_NAMES.ECG,
      FEATURE_AVAILABILITY_FEATURE_NAMES.IHRN,
    ].includes(featureName);

    if (!isECGOrIHRN) return null;

    if (ECG_IHRN_UNAVAILABLE_COUNTRIES.has(country)) {
      return {
        isAvailable: false,
        isComingSoon: false,
        isUnavailable: true,
      };
    }

    return null;
  }

  getFeatureStatus({
    featureName,
    region,
    country,
  }: {
    featureName: FeatureName;
    region: Region;
    country: string;
  }): FeatureStatus {
    // Check for special cases (based on country)
    const overrides = this.handleOverrides(featureName, country.toUpperCase());
    if (overrides) return overrides;

    // For all other countries/features
    const isAvailable = this.isAvailable(featureName, region);
    const isComingSoon = this.isComingSoon(featureName, region);
    const isUnavailable = this.isUnavailable(featureName, region);

    return { isAvailable, isComingSoon, isUnavailable };
  }

  isCompatibleWithTier(featureName: FeatureName, tierName: string): boolean {
    const feature = this.data.find(
      (featureAvailability) =>
        featureAvailability.feature.featureName === featureName,
    );

    if (!feature) return false;

    return feature.tierAvailability.items.some(
      (item) => item.tierName === tierName,
    );
  }
}
